import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';

import { CardBody, CardFooter, CompensationForm } from 'components/common';
import { getCompensationByIdAdmin } from 'services/odata-api.service';
import { getUserCompensationById } from 'services/users.service';
import { CompensationStatus, CompensationType, Path, StatusCode } from 'enums';
import type { Compensation } from 'types';

import { CompensationView, EditCompensationStatusModal, StatusActions } from './components';
import { EditorButtons } from '../SettingsPage';
import { CompensationEditMode } from './enums';
import { useSubmitRequest } from './hooks';
import { useCompensationPage } from '../CompensationPage/CompensationPage';
import { getCompensationAvailableEditMode } from './helpers';
import styles from './Details.module.scss';
import { Spinner } from '../common/components/Spinner/Spinner';

const formId = 'details';

export const Details = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    isEditMode,
    setIsEditMode,
    availableEditMode,
    setAvailableEditMode,
    permissions,
    compensation,
    setCompensation,
    isAccountPage,
  } = useCompensationPage();
  const [updatingStatus, setUpdatingStatus] = useState<CompensationStatus>();
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isAttachmentsExpired = useMemo(() => {
    if (!compensation?.createdOn) return false;

    if (compensation.type !== CompensationType.Medical) return false;

    const created = dayjs(compensation.createdOn);
    const isAfter2026 = created.year() > 2025;
    const twoYearsPassed = !dayjs().isBefore(created.add(2, 'year'));

    return isAfter2026 && twoYearsPassed;
  }, [compensation?.createdOn, compensation?.type]);

  const fetchCompensationById = useCallback(
    async (requestId: string) => {
      setIsLoading(true);
      try {
        let comp: Compensation | undefined;

        if (permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission) {
          comp = await getCompensationByIdAdmin(requestId);
        } else {
          comp = await getUserCompensationById(requestId);
        }

        if (!comp) {
          setCompensation(undefined);
          navigate(`/${Path.NotFound}`, { replace: true });
        } else {
          setCompensation(comp);
        }
      } catch (error) {
        setCompensation(undefined);

        if (axios.isAxiosError(error) && error.response?.status === StatusCode.NotFound) {
          navigate(`/${Path.NotFound}`, { replace: true });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, permissions, setCompensation],
  );

  const handleSuccessSubmit = useCallback(() => {
    if (id) {
      fetchCompensationById(id);
    }
  }, [fetchCompensationById, id]);

  const { handleSubmit } = useSubmitRequest(
    handleSuccessSubmit,
    availableEditMode === CompensationEditMode.User,
    compensation?.attachments || [],
  );

  useEffect(handleSuccessSubmit, [fetchCompensationById, id, handleSuccessSubmit]);

  useEffect(() => {
    const nextAvailableEditMode = compensation
      ? getCompensationAvailableEditMode(compensation, isAccountPage, permissions)
      : CompensationEditMode.None;

    setAvailableEditMode(nextAvailableEditMode);
  }, [permissions, compensation, setAvailableEditMode, isAccountPage]);

  const isStatusActionsVisible = useMemo(
    () =>
      !isEditMode &&
      (availableEditMode === CompensationEditMode.Admin || availableEditMode === CompensationEditMode.Manager),
    [isEditMode, availableEditMode],
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!compensation) {
    return null;
  }

  return (
    <>
      <CardBody>
        {isEditMode && availableEditMode !== CompensationEditMode.None ? (
          <CompensationForm
            compensation={compensation}
            submit={handleSubmit}
            formId={formId}
            isUserMode={availableEditMode === CompensationEditMode.User}
            onDisabledChange={setSaveDisabled}
            isAttachmentsExpired={isAttachmentsExpired}
          />
        ) : (
          <CompensationView compensation={compensation} isAttachmentsExpired={isAttachmentsExpired} />
        )}
      </CardBody>
      {availableEditMode !== CompensationEditMode.None && (isStatusActionsVisible || isEditMode) && (
        <CardFooter>
          <div className={styles.actionsContainer}>
            {isEditMode && (
              <EditorButtons
                isEditMode={isEditMode}
                onCancelClick={() => setIsEditMode(false)}
                onEditClick={() => setIsEditMode(true)}
                id={formId}
                disabledSave={saveDisabled}
              />
            )}
            {isStatusActionsVisible && (
              <StatusActions currentStatus={compensation.status} onAction={setUpdatingStatus} />
            )}
          </div>
        </CardFooter>
      )}
      <EditCompensationStatusModal
        nextStatus={updatingStatus}
        compensation={compensation}
        submit={handleSubmit}
        close={() => setUpdatingStatus(undefined)}
      />
    </>
  );
};
